import React from 'react';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/home/Footer';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaWhatsapp, FaCalendarAlt, FaUserFriends, FaGlobe } from 'react-icons/fa';
import MetaTags from '../components/MetaTags';

const Contact: React.FC = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    tourPreference: '',
    travelers: '',
    arrivalDate: '',
    departureDate: '',
    message: ''
  });

  useEffect(() => {
    // Scroll to the relevant section based on hash
    const hash = location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch("https://formspree.io/f/xkgjvqwe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // Show success message
        alert('Thank you for your inquiry! We will contact you within 24 hours.');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          tourPreference: '',
          travelers: '',
          arrivalDate: '',
          departureDate: '',
          message: ''
        });
      } else {
        throw new Error("Error submitting form");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      alert("There was an error submitting your request. Please try again or contact us directly.");
    }
  };

  // SEO structured data for contact page
  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Rion Wildlife Safaris",
    "description": "Get in touch with Professor Rion Haraeb and our team for an unforgettable safari experience in Namibia, Botswana and Victoria Falls.",
    "mainEntity": {
      "@type": "TravelAgency",
      "name": "Rion Wildlife Safaris",
      "telephone": "+264 81 323 6891",
      "email": "rionwildlifesafaris@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Sandwich Street",
        "postOfficeBoxNumber": "P.O. Box 2221",
        "addressLocality": "Walvisbay",
        "addressCountry": "Namibia"
      }
    }
  };

  return (
    <>
      <MetaTags
        title="Contact Us | Rion Wildlife Safaris"
        description="Contact Professor Rion Haraeb and our team to plan your safari adventure in Namibia, Botswana and Victoria Falls. Available via email, phone, and WhatsApp."
        canonicalUrl="https://rionwildlifesafaris.com/contact"
        structuredData={contactStructuredData}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section - Using the new organized image path */}
        <div className="relative h-[65vh] min-h-[500px] flex items-center justify-center bg-gray-800 overflow-hidden">
          <img
            src="/images/contact/contacthero.jpeg"
            alt="Contact Rion Wildlife Safaris"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-black/50 pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"></div>
          {/* Navbar */}
          <header className="absolute top-0 left-0 right-0 z-50">
            <Navbar />
          </header>

          {/* Hero content */}
          <div className="relative z-10 flex items-center justify-center h-full">
            <div className="text-center text-white px-4 max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white max-w-4xl leading-tight font-display animate-fade-up">Contact Us</h1>
              <p className="text-xl text-white/90 max-w-2xl mb-8 animate-fade-up animation-delay-200">
                Get in touch with Professor Rion Haraeb and our team for an unforgettable safari experience
              </p>
            </div>
          </div>

          {/* Bottom gradient */}
          <div className="absolute bottom-0 w-full h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 animate-fade-up">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <FaMapMarkerAlt className="text-2xl text-amber-700" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Location</h3>
              <p className="text-gray-600">Sandwich Street</p>
              <p className="text-gray-600">P.O. Box 2221</p>
              <p className="text-gray-600">Walvisbay, Namibia</p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 animate-fade-up animation-delay-200">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <FaPhone className="text-2xl text-amber-700" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Phone</h3>
              <p className="text-gray-600">+264 81 323 6891</p>
              <div className="flex items-center mt-3">
                <a href="https://wa.me/264813236891" className="flex items-center text-green-600 hover:text-green-700 hover:underline transition-colors" target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp className="mr-2" aria-hidden="true" />
                  <span>WhatsApp Available</span>
                </a>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 animate-fade-up animation-delay-400">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <FaEnvelope className="text-2xl text-amber-700" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Contact Online</h3>
              <div className="space-y-2">
                <a href="https://mail.google.com/mail/?view=cm&fs=1&to=rionwildlifesafaris@gmail.com&su=Safari%20Inquiry&body=Hello%20Mr.%20Rion,%0A%0AI'm%20interested%20in%20learning%20more%20about%20your%20safari%20tours.%0A%0AThank%20you." className="text-amber-700 hover:text-amber-800 hover:underline transition-colors block" target="_blank" rel="noopener noreferrer">
                  <span className="font-medium">Email:</span> rionwildlifesafaris@gmail.com
                </a>
                <a href="https://www.rionwildlifesafaris.com" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:text-amber-800 hover:underline transition-colors block">
                  <span className="font-medium">Website:</span> www.rionwildlifesafaris.com
                </a>
              </div>
            </div>
          </div>

          {/* Booking Inquiries Section */}
          <div id="booking" className="mb-16 scroll-mt-24">
            <div className="text-center mb-10 animate-fade-up">
              <h2 className="text-3xl font-bold mb-4 font-display text-gray-900">Booking Inquiries</h2>
              <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full mb-4"></div>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Please fill out the form below and our team will get back to you within 24 hours to discuss your safari experience.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl animate-fade-up animation-delay-200">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUserFriends className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                        required
                        placeholder="John Doe"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaEnvelope className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                        required
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Safari Tour</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaGlobe className="text-gray-400" />
                    </div>
                    <select
                      name="tourPreference"
                      value={formData.tourPreference}
                      onChange={handleInputChange}
                      className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 appearance-none bg-white"
                    >
                      <option value="">Select a tour</option>
                      <option value="namibia-highlights">Namibia Highlights Safari</option>
                      <option value="big-five">Big Five Safari</option>
                      <option value="namwild">Namwild Safari</option>
                      <option value="walvisbay">Walvisbay Excursions</option>
                      <option value="victoria-falls">Namibia, Botswana & Victoria Falls</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Travelers</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUserFriends className="text-gray-400" />
                    </div>
                    <input
                      type="number"
                      name="travelers"
                      value={formData.travelers}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                      required
                      placeholder="2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Travel Dates</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaCalendarAlt className="text-gray-400" />
                      </div>
                      <input
                        type="date"
                        name="arrivalDate"
                        value={formData.arrivalDate}
                        onChange={handleInputChange}
                        className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                        required
                        placeholder="Arrival"
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaCalendarAlt className="text-gray-400" />
                      </div>
                      <input
                        type="date"
                        name="departureDate"
                        value={formData.departureDate}
                        onChange={handleInputChange}
                        className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                        required
                        placeholder="Departure"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
                    placeholder="Tell us about your interests, special requests, or any questions you may have..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full md:w-auto px-8 py-3 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all font-medium"
                >
                  Submit Inquiry
                </button>
              </form>
            </div>
          </div>

          {/* Support Section */}
          <div id="support" className="scroll-mt-24">
            <div className="text-center mb-10 animate-fade-up">
              <h2 className="text-3xl font-bold mb-4 font-display text-gray-900">Support</h2>
              <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full mb-4"></div>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Find answers to common questions or reach out to our support team for assistance.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl animate-fade-up animation-delay-200">
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-6 text-gray-900 border-b border-gray-200 pb-3">Frequently Asked Questions</h3>
                <div className="space-y-6">
                  <div className="bg-gray-50 p-5 rounded-lg animate-fade-up animation-delay-300">
                    <h4 className="font-medium mb-2 text-amber-800">What languages are the tours conducted in?</h4>
                    <p className="text-gray-600">Our tours are conducted in both English and German by Professor Rion Haraeb, who is fluent in both languages.</p>
                  </div>
                  <div className="bg-gray-50 p-5 rounded-lg animate-fade-up animation-delay-400">
                    <h4 className="font-medium mb-2 text-amber-800">What is included in the tour price?</h4>
                    <p className="text-gray-600">Tour prices include accommodation, transportation, guided activities, and most meals. Specific inclusions vary by tour and are detailed on each tour page.</p>
                  </div>
                  <div className="bg-gray-50 p-5 rounded-lg animate-fade-up animation-delay-500">
                    <h4 className="font-medium mb-2 text-amber-800">What is the booking and cancellation policy?</h4>
                    <p className="text-gray-600">We require a 70% deposit to secure your booking. Full payment is due 60 days before departure. Please contact us for detailed cancellation terms.</p>
                  </div>
                </div>
              </div>

              <div className="animate-fade-up animation-delay-600">
                <h3 className="text-xl font-semibold mb-6 text-gray-900 border-b border-gray-200 pb-3">Need Additional Help?</h3>
                <p className="text-gray-600 mb-6">Our support team is available to assist you with any questions or concerns you may have about our safari experiences.</p>
                <div className="flex flex-col md:flex-row gap-4">
                  <a
                    href="tel:+264813236891"
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
                    aria-label="Call our support team"
                  >
                    <FaPhone aria-hidden="true" />
                    <span>Call Support</span>
                  </a>
                  <a
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=rionwildlifesafaris@gmail.com&su=Safari%20Support%20Request&body=Hello%20Mr.%20Rion,%0A%0AI%20need%20some%20assistance%20with%20the%20following:%0A%0AThank%20you."
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
                    aria-label="Email our support team"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaEnvelope aria-hidden="true" />
                    <span>Email Support</span>
                  </a>
                  <a
                    href="https://wa.me/264813236891"
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    aria-label="Contact our support team via WhatsApp"
                  >
                    <FaWhatsapp aria-hidden="true" />
                    <span>WhatsApp Us</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Contact; 